/**
 * The one diff over a mapped tabular config's public shape. The builder runs
 * it between a base version and a proposal (`diffTabularConfigProposal`); the
 * browser runs it between two saved versions (*diff to previous*). Both go
 * through here so the JSON-path entries a draft shows (`parser.script`,
 * `output.tables[0].columns[3].type`, `checks[1].sql`,
 * `parser.fingerprint.headers.Sheet1.cells[2]`) are the ones a version shows.
 *
 * Approval stamps (`approvedBy`, `changeReason`, `history`, `verifiedAt`,
 * `imageVersion`) and `examples` are not part of the public shape.
 */

export interface TabularConfigDiffEntry {
  path: string
  before?: unknown
  after?: unknown
}

export interface TabularConfigDiff {
  entries: TabularConfigDiffEntry[]
  truncated: boolean
}

export const MAX_TABULAR_CONFIG_DIFF_ENTRIES = 200

interface TabularConfigPublicScript {
  entrypoint: string
  script: string
  usage?: string
}

/** What a saved definition and a builder proposal have in common, structurally. */
export interface TabularConfigPublicFields {
  description?: string
  output: unknown
  checks?: unknown
  pageTables?: unknown
  input?: unknown
  maxAgentTurns?: number
  parser:
    | {
        kind: 'agent'
        preprocess?: TabularConfigPublicScript
        finalize?: TabularConfigPublicScript
        helpers?: readonly TabularConfigPublicScript[]
        modules?: readonly TabularConfigPublicScript[]
      }
    | { kind: 'script'; script: string; entrypoint: string; fingerprint: unknown }
}

export function tabularConfigPublicShape(
  config: TabularConfigPublicFields
): Record<string, unknown> {
  return {
    description: config.description,
    output: config.output,
    checks: config.checks,
    pageTables: config.pageTables,
    input: config.input,
    maxAgentTurns: config.maxAgentTurns,
    parser:
      config.parser.kind === 'agent'
        ? {
            kind: 'agent',
            preprocess: publicScript(config.parser.preprocess),
            finalize: publicScript(config.parser.finalize),
            helpers: config.parser.helpers?.map(publicScript),
            modules: config.parser.modules?.map(publicScript)
          }
        : {
            kind: 'script',
            script: config.parser.script,
            entrypoint: config.parser.entrypoint,
            fingerprint: config.parser.fingerprint
          }
  }
}

function publicScript(
  file: TabularConfigPublicScript | undefined
): TabularConfigPublicScript | undefined {
  return file && { entrypoint: file.entrypoint, script: file.script, usage: file.usage }
}

export function diffTabularConfigs(
  before: TabularConfigPublicFields,
  after: TabularConfigPublicFields,
  maxEntries = MAX_TABULAR_CONFIG_DIFF_ENTRIES
): TabularConfigDiff {
  const entries: TabularConfigDiffEntry[] = []
  collectDiff(
    canonicalJsonValue(tabularConfigPublicShape(before)),
    canonicalJsonValue(tabularConfigPublicShape(after)),
    '',
    entries
  )
  const limit = Number.isSafeInteger(maxEntries) && maxEntries >= 0 ? maxEntries : 0
  return { entries: entries.slice(0, limit), truncated: entries.length > limit }
}

/**
 * The value with object keys sorted and `undefined` members dropped, so equal
 * documents compare and serialize equal. Anything JSON cannot carry throws.
 */
export function canonicalJsonValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalJsonValue)
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, child]) => child !== undefined)
        .sort(([a], [b]) => compareText(a, b))
        .map(([key, child]) => [key, canonicalJsonValue(child)])
    )
  }
  if (
    value === null ||
    typeof value === 'string' ||
    typeof value === 'boolean' ||
    (typeof value === 'number' && Number.isFinite(value))
  ) {
    return value
  }
  throw new Error(`value is not canonical JSON: ${String(value)}`)
}

function collectDiff(
  before: unknown,
  after: unknown,
  path: string,
  output: TabularConfigDiffEntry[]
): void {
  if (Object.is(before, after)) return
  if (Array.isArray(before) && Array.isArray(after)) {
    const length = Math.max(before.length, after.length)
    for (let index = 0; index < length; index += 1) {
      collectDiff(before[index], after[index], `${path}[${index}]`, output)
    }
    return
  }
  if (isRecord(before) && isRecord(after)) {
    const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])].sort(compareText)
    for (const key of keys) {
      collectDiff(before[key], after[key], path ? `${path}.${key}` : key, output)
    }
    return
  }
  output.push({
    path: path || '$',
    ...(before === undefined ? {} : { before }),
    ...(after === undefined ? {} : { after })
  })
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value)
}

function compareText(a: string, b: string): number {
  return a < b ? -1 : a > b ? 1 : 0
}
